import * as Yup from 'yup';
import {
    BIRTHDAY_CUTOFF,
    Division,
    DIVISION_LIMITATION,
    DIVISION_STYLE,
    DivisionToInsert,
    Event,
    PrettifyType,
    STATUS,
    WeighInItem,
} from '@type/db-types';

interface EventFormPure
    extends Omit<
            Event,
            'id' | 'createdAt' | 'updatedAt' | 'divisions' | 'registrantsCount' | 'weighInAt'
        >,
        Partial<Pick<Event, 'id' | 'createdAt' | 'updatedAt'>> {
    divisions: DivisionToInsert[] | Division[];
    stripeDetailsSubmitted: boolean;
    weighInAt: WeighInItem[] | null; // technically it can be any JSON, but we want to enforce this type when possible
    id?: string;
}

export type EventForm = PrettifyType<EventFormPure>;

export const getDefaultNewEvent = (
    existingEvent: Event | Partial<EventForm> | undefined,
    stripeDetailsSubmitted: boolean | undefined,
    userId: string | undefined,
): EventForm => {
    const START_AT_DEFAULT = new Date();
    START_AT_DEFAULT.setHours(11, 0, 0, 0);

    return {
        birthdayCutoff: BIRTHDAY_CUTOFF.EVENT_START,
        createdByUserId: userId || 'no-user-id',
        description: '',
        divisions: [],
        fee: 0,
        imageUrl: '',
        isPrivate: false,
        location: '',
        maxEntries: null, // if not set (null), there is no limit
        name: '',
        payToUserId: userId || '',
        placeId: '',
        status: STATUS.ACTIVE,
        stripeDetailsSubmitted: !!stripeDetailsSubmitted,
        waiver: '',
        weightTolerance: null,
        startAt: START_AT_DEFAULT.toLocaleString(),
        // FIXME: review hours vs date formatting in DB, do we have all the values we need?
        weighInAt: [],
        ...(typeof existingEvent === 'object' ? existingEvent : {}),
    };
};

export const newEventSchema: Yup.ObjectSchema<EventForm> = Yup.object({
    birthdayCutoff: Yup.string().oneOf(Object.values(BIRTHDAY_CUTOFF)).required(),
    createdAt: Yup.string().optional(),
    createdByUserId: Yup.string().required(),
    description: Yup.string().required(),
    divisions: Yup.array<EventForm['divisions']>()
        .of(
            Yup.object({
                bracketStyle: Yup.string().oneOf(Object.values(DIVISION_STYLE)).required(),
                classes: Yup.array()
                    .when('bracketStyle', ([bracketStyle], schema) =>
                        bracketStyle === DIVISION_STYLE.FIXED_WEIGHT_CLASSES
                            ? schema
                                  .of(
                                      Yup.object({
                                          divisionId: Yup.string().ensure(),
                                          maxEntries: Yup.number().nullable().defined(),
                                          maxWeight: Yup.string().required(),
                                          name: Yup.string().required(),
                                          sortIndex: Yup.number().required(), // auto-generated by DB to current time if missing
                                          status: Yup.string()
                                              .oneOf(Object.values(STATUS))
                                              .required(),
                                      }),
                                  )
                                  .required()
                            : schema.notRequired(),
                    )
                    .required(),
                entryLimitation: Yup.string()
                    .nonNullable()
                    .oneOf(Object.values(DIVISION_LIMITATION))
                    .required(),
                maxAge: Yup.number().nullable().defined(),
                minAge: Yup.number().nullable().defined(),
                name: Yup.string().min(1, `Division name required`).required(),
                sortIndex: Yup.number().required(), // auto-generated by DB to current time if missing
                status: Yup.string().oneOf(Object.values(STATUS)).required(),
            }),
        )
        .min(1, 'At least one division is required')
        .required(),
    fee: Yup.number().min(1, 'An event registration fee is required').required(),
    id: Yup.string().optional(),
    imageUrl: Yup.string().ensure(),
    isPrivate: Yup.boolean().required(),
    location: Yup.string().min(3, 'Event location is required').required(),
    maxEntries: Yup.number().nullable().defined(),
    name: Yup.string().required(),
    payToUserId: Yup.string().required(),
    placeId: Yup.string().required(),
    startAt: Yup.string().required(),
    status: Yup.string().oneOf(Object.values(STATUS)).required(),
    stripeDetailsSubmitted: Yup.boolean().required(),
    updatedAt: Yup.string().optional(),
    waiver: Yup.string().required(),
    weighInAt: Yup.array<WeighInItem>()
        .of(
            Yup.object({
                location: Yup.string().defined(),
                note: Yup.string().defined(),
                placeId: Yup.string().defined(),
                weighInAtDateTime: Yup.string().required(),
            }),
        )
        .defined(),
    weightTolerance: Yup.number().nullable().defined(),
});
